import React, { useCallback, useMemo } from 'react'
import { Row } from 'antd'
import { isEmpty, map } from 'lodash'
import PropTypes from 'prop-types'

import {
  AiHighlightDecoratedComponent,
  JsonDescriptorCollapse,
  ReadMore,
  RichTextHighlightingInput,
  TextDescriptor,
} from '../../../common/components/sections'
import { handleAiHighlightStrategy } from '../../../common/helpers'

import { MENU_TITLE_FIELD_NAMES } from '../../../menu-title/constants'
import { menuTitlePropType } from '../../../menu-title/prop-types'
import { formatDishMenuTitleOriginFieldPrefix } from '../../utils'

const DishMenuTitleDetailsCell = ({
  menuTitles,
  menuTitleFieldName,
  highlights,
}) => {
  const renderDetail = useCallback(
    menuTitle => {
      if (menuTitleFieldName === MENU_TITLE_FIELD_NAMES.ADDON_DESCRIPTORS) {
        return (
          <TextDescriptor
            value={menuTitle[menuTitleFieldName]}
            highlights={highlights}
            prefix={formatDishMenuTitleOriginFieldPrefix(
              'menuTitleAddonDescriptors',
              menuTitle.id,
            )}
          />
        )
      }

      if (menuTitleFieldName === MENU_TITLE_FIELD_NAMES.ALLERGEN_DESCRIPTORS) {
        return (
          <JsonDescriptorCollapse
            value={menuTitle[menuTitleFieldName]}
            highlights={highlights}
            prefix={formatDishMenuTitleOriginFieldPrefix(
              'menuTitleAllergenDescriptors',
              menuTitle.id,
            )}
          />
        )
      }

      if (menuTitleFieldName === MENU_TITLE_FIELD_NAMES.DIET_DESCRIPTORS) {
        return (
          <JsonDescriptorCollapse
            value={menuTitle[menuTitleFieldName]}
            highlights={highlights}
            prefix={formatDishMenuTitleOriginFieldPrefix(
              'menuTitleDietDescriptors',
              menuTitle.id,
            )}
          />
        )
      }

      return (
        <RichTextHighlightingInput
          value={menuTitle.styledDescription || menuTitle.description}
          highlightStrategy={handleAiHighlightStrategy}
          highlights={
            highlights[
              formatDishMenuTitleOriginFieldPrefix(
                'menuTitleDescription',
                menuTitle.id,
              )
            ]
          }
          decoratorComponent={AiHighlightDecoratedComponent}
          isReadOnly
        />
      )
    },
    [menuTitleFieldName, highlights],
  )

  const detailComponent = useMemo(
    () =>
      map(menuTitles, menuTitle => {
        if (
          menuTitleFieldName !== MENU_TITLE_FIELD_NAMES.DESCRIPTION &&
          isEmpty(menuTitle[menuTitleFieldName])
        ) {
          return null
        }

        return (
          <Row key={menuTitle.id}>
            <strong>
              {menuTitleFieldName === MENU_TITLE_FIELD_NAMES.DESCRIPTION ? (
                <RichTextHighlightingInput
                  value={menuTitle.styledTitle || menuTitle.title}
                  highlightStrategy={handleAiHighlightStrategy}
                  highlights={
                    highlights[
                      formatDishMenuTitleOriginFieldPrefix(
                        'menuTitleName',
                        menuTitle.id,
                      )
                    ]
                  }
                  decoratorComponent={AiHighlightDecoratedComponent}
                  isReadOnly
                />
              ) : (
                menuTitle.title
              )}
            </strong>
            :{renderDetail(menuTitle)}
          </Row>
        )
      }),
    [highlights, menuTitleFieldName, menuTitles, renderDetail],
  )

  return <ReadMore maxHeight={170} component={detailComponent} />
}

DishMenuTitleDetailsCell.propTypes = {
  menuTitles: PropTypes.arrayOf(menuTitlePropType).isRequired,
  menuTitleFieldName: PropTypes.oneOf([
    MENU_TITLE_FIELD_NAMES.DESCRIPTION,
    MENU_TITLE_FIELD_NAMES.ADDON_DESCRIPTORS,
    MENU_TITLE_FIELD_NAMES.ALLERGEN_DESCRIPTORS,
    MENU_TITLE_FIELD_NAMES.DIET_DESCRIPTORS,
  ]),
  highlights: PropTypes.object,
}

DishMenuTitleDetailsCell.defaultProps = {
  menuTitleFieldName: MENU_TITLE_FIELD_NAMES.DESCRIPTION,
  highlights: {},
}

export default DishMenuTitleDetailsCell
